import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-License-Identifier: AGPL-3.0-or-later
import { useEffect, useId, useRef, useState } from 'react';
import { ExtensionUrl, findExtension, formatAddressOneLine, parseAddressString, serializeAddress, } from '@intakeform/fhir';
import { Input } from '../../primitives/Input.js';
import { describedBy, FieldHost } from '../FieldHost.js';
import { lookupZip } from './zip-lookup.js';
function readGoogleMaps() {
    if (typeof window === 'undefined')
        return undefined;
    return window.google;
}
/** Map a `PlacesResult` to our FhirAddress shape. */
export function placeToFhirAddress(place) {
    const components = place.address_components ?? [];
    const get = (type, useShort = false) => {
        const c = components.find((cc) => cc.types.includes(type));
        if (c === undefined)
            return undefined;
        return useShort ? c.short_name : c.long_name;
    };
    const streetNumber = get('street_number');
    const route = get('route');
    const subpremise = get('subpremise');
    const line1 = [streetNumber, route].filter((s) => typeof s === 'string').join(' ');
    const lines = [];
    if (line1.length > 0)
        lines.push(line1);
    if (typeof subpremise === 'string' && subpremise.length > 0)
        lines.push(`Unit ${subpremise}`);
    const city = get('locality') ?? get('postal_town') ?? get('administrative_area_level_2') ?? undefined;
    const state = get('administrative_area_level_1', true);
    const postalCode = get('postal_code');
    const country = get('country', true);
    const address = { use: 'home', type: 'physical' };
    if (lines.length > 0)
        address.line = lines;
    if (city !== undefined)
        address.city = city;
    if (state !== undefined)
        address.state = state;
    if (postalCode !== undefined)
        address.postalCode = postalCode;
    if (country !== undefined)
        address.country = country;
    if (typeof place.formatted_address === 'string')
        address.text = place.formatted_address;
    return address;
}
function asString(v) {
    return typeof v === 'string' ? v : '';
}
function readCountryBias(item) {
    const ext = findExtension(item, ExtensionUrl.addressCountry);
    const code = ext?.valueCode;
    return typeof code === 'string' && code.length > 0 ? code : undefined;
}
export function AddressField(props) {
    const { item, value, errors, disabled, onChange } = props;
    const id = useId();
    const inputRef = useRef(null);
    const stored = asString(value);
    const parsed = stored.length > 0 ? parseAddressString(stored) : undefined;
    const [displayValue, setDisplayValue] = useState(() => {
        if (parsed !== undefined)
            return formatAddressOneLine(parsed);
        return stored;
    });
    const [autocompleteReady, setAutocompleteReady] = useState(false);
    const countryBias = readCountryBias(item);
    // Wire Google Places Autocomplete when the API is available. The
    // listener is torn down on unmount; the component degrades to a plain
    // text input when `window.google.maps.places` is undefined.
    useEffect(() => {
        if (disabled === true)
            return;
        const input = inputRef.current;
        if (input === null)
            return;
        const google = readGoogleMaps();
        const places = google?.maps?.places;
        if (places === undefined)
            return;
        const options = {
            fields: ['address_components', 'formatted_address'],
            types: ['address'],
        };
        if (countryBias !== undefined) {
            options.componentRestrictions = { country: countryBias };
        }
        const autocomplete = new places.Autocomplete(input, options);
        setAutocompleteReady(true);
        const subscription = autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            const address = placeToFhirAddress(place);
            const oneLine = formatAddressOneLine(address);
            setDisplayValue(oneLine);
            onChange(serializeAddress(address));
        });
        return () => subscription.remove();
    }, [countryBias, disabled, onChange]);
    return (_jsx(FieldHost, { item: item, errors: errors, controlId: id, children: (ctx) => (_jsxs(_Fragment, { children: [_jsx(Input, { id: id, ref: inputRef, type: "text", value: displayValue, disabled: disabled === true, required: item.required === true, placeholder: autocompleteReady ? 'Start typing an address…' : 'Enter your address', autoComplete: autocompleteReady ? 'off' : 'street-address', "aria-invalid": ctx.hasError || undefined, "aria-describedby": describedBy(ctx), onChange: (e) => {
                        const next = e.currentTarget.value;
                        setDisplayValue(next);
                        if (next === '') {
                            onChange(undefined);
                            return;
                        }
                        // Manual / Places-unavailable path. When the patient
                        // types exactly a 5-digit US ZIP, autofill city + state
                        // from the bundled `zipcodes` dataset and persist the
                        // structured FhirAddress (in-3uo). Anything else is kept
                        // as raw text — the submit path treats unstructured text
                        // as the Address.text field.
                        const zipHit = lookupZip(next);
                        if (zipHit !== null) {
                            const address = {
                                use: 'home',
                                type: 'physical',
                                city: zipHit.city,
                                state: zipHit.state,
                                postalCode: next.trim(),
                                text: `${zipHit.city}, ${zipHit.state} ${next.trim()}`,
                            };
                            onChange(serializeAddress(address));
                            return;
                        }
                        onChange(next);
                    } }), !autocompleteReady ? (_jsx("p", { className: "muted", style: { fontSize: 12, marginTop: 4 }, children: "Type your address manually. Suggestions need a Google API key." })) : null] })) }));
}
/**
 * Predicate the field-type registry uses to discriminate the address
 * field from a generic short-text item. An item is an address field
 * when it is a `string` carrying the `address: true` extension.
 */
export function isAddressItem(item) {
    if (item.type !== 'string')
        return false;
    const ext = findExtension(item, ExtensionUrl.address);
    return ext?.valueBoolean === true;
}
//# sourceMappingURL=AddressField.js.map